import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";

/** ========= Helpers ========= */
const fmtDate = (ts) => {
  if (!ts) return "-";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString("tr-TR");
  try { return new Date(ts).toLocaleString("tr-TR"); } catch { return "-"; }
};

const dayDiff = (start, end) => {
  const s = start?.seconds ? new Date(start.seconds * 1000) : null;
  const e = end?.seconds ? new Date(end.seconds * 1000) : null;
  if (!s || !e) return null;
  return Math.round((e - s) / (1000 * 60 * 60 * 24));
};

// Türkçe uyumlu slug (başlık normalize)
const toSlugTR = (str = "") => {
  const map = { 'Ç':'c','Ö':'o','Ş':'s','İ':'i','I':'i','Ğ':'g','Ü':'u','ç':'c','ö':'o','ş':'s','ı':'i','ğ':'g','ü':'u' };
  const replaced = (str || "").replace(/[ÇÖŞİIĞÜçöşığü]/g, ch => map[ch] || ch);
  return replaced
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Ortak input/select stili
const fieldStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  boxSizing: "border-box",
};

const th = { border: "1px solid #cbd5e1", padding: 10, textAlign: "left", fontSize: 14, background: "#334155", color: "#fff" };
const td = { border: "1px solid #cbd5e1", padding: 10, fontSize: 13 };
const chip = { padding: "2px 8px", borderRadius: 999, background: "#e2e8f0", display: "inline-block", fontSize: 12 };

/** ========= Autocomplete (Plaka) ========= */
const Autocomplete = ({ value, onCommit, options, placeholder = "Tüm plakalar" }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(value || "");
  const wrapRef = useRef(null);

  useEffect(() => { setInput(value || ""); }, [value]);

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onCommit((input || "").trim());
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [input, onCommit]);

  const filtered = useMemo(() => {
    const s = (input || "").toLowerCase();
    if (!s) return options.slice(0, 50);
    return options.filter(o => (o || "").toLowerCase().includes(s)).slice(0, 50);
  }, [input, options]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onCommit((input || "").trim());
      setOpen(false);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={wrapRef}>
      <input
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={fieldStyle}
      />
      {open && filtered.length > 0 && (
        <ul
          style={{
            position: "absolute",
            zIndex: 50,
            listStyle: "none",
            margin: "4px 0 0 0",
            padding: 0,
            width: "100%",
            maxHeight: 220,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)"
          }}
        >
          {filtered.map((opt, i) => (
            <li
              key={`${opt}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onCommit(opt); setInput(opt); setOpen(false); }}
              style={{ padding: "8px 10px", cursor: "pointer" }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** ========= Page ========= */
const ResolvedIssues = () => {
  // Veriler
  const [issues, setIssues] = useState([]);
  const [terminMap, setTerminMap] = useState({});    // termin koleksiyonu
  const [titleMap, setTitleMap] = useState({});      // termin_title_map (id=slug)

  // Filtreler
  const [fTesis, setFTesis] = useState("");
  const [fPlate, setFPlate] = useState("");
  const [fText, setFText] = useState("");
  const [fCreator, setFCreator] = useState("");
  const [fResolver, setFResolver] = useState("");
  const [fFrom, setFFrom] = useState(""); // yyyy-mm-dd
  const [fTo, setFTo] = useState("");
  const [onTimeState, setOnTimeState] = useState("all"); // all | ontime | late
  const [fCategory, setFCategory] = useState(""); // "" tümü | "__none__" kategorisiz | kategoriId

  // Seçenekler
  const [plates, setPlates] = useState([]);
  const [tesisler, setTesisler] = useState([]);
  const [loading, setLoading] = useState(true);

  // Çözülen issues
  useEffect(() => {
    const qResolved = query(collection(db, "issues"), where("resolved", "==", true));
    const unsub = onSnapshot(
      qResolved,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // çözüm tarihine göre yeni → eski
        data.sort((a, b) => (b.resolvedAt?.seconds ?? -1) - (a.resolvedAt?.seconds ?? -1));
        setIssues(data);
        setPlates([...new Set(data.map(x => x.plate).filter(Boolean))].sort());
        setTesisler([...new Set(data.map(x => x.tesis || "Belirtilmemiş"))].sort());
        setLoading(false);
      },
      (err) => {
        console.error("Resolved snapshot error:", err?.message || err);
        alert("Çözülenler sorgusunda hata: " + (err?.message || err));
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Termin kategorileri
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin"), (snap) => {
      const m = {};
      snap.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; });
      setTerminMap(m);
    }, (err) => console.error("termin snapshot error:", err?.message || err));
    return () => unsub();
  }, []);

  // Başlık→Kategori eşlemesi
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "termin_title_map"), (snap) => {
      const m = {};
      snap.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; }); // id=slug
      setTitleMap(m);
    }, (err) => console.error("title_map snapshot error:", err?.message || err));
    return () => unsub();
  }, []);

  // Bir issue için kategori bul: 1) issue.terminCategoryId varsa o; 2) yoksa başlık slug’ından
  const getCategoryForIssue = (it) => {
    if (it.terminCategoryId && terminMap[it.terminCategoryId]) return terminMap[it.terminCategoryId];
    const slug = toSlugTR((it.issue || "").trim());
    const mapDoc = slug ? titleMap[slug] : null;
    if (mapDoc && mapDoc.categoryId && terminMap[mapDoc.categoryId]) {
      return terminMap[mapDoc.categoryId];
    }
    return null;
  };

  // Filtreleme
  const filtered = useMemo(() => {
    return issues.filter((it) => {
      if (fTesis && (it.tesis || "Belirtilmemiş") !== fTesis) return false;

      if (fPlate) {
        const val = fPlate.toLowerCase();
        if (!((it.plate || "").toLowerCase().includes(val))) return false;
      }

      if (fCreator && !(it.createdBy || "").toLowerCase().includes(fCreator.toLowerCase())) return false;
      if (fResolver && !(it.resolvedBy || "").toLowerCase().includes(fResolver.toLowerCase())) return false;
      if (fText && !(it.issue || "").toLowerCase().includes(fText.toLowerCase())) return false;

      // Kategori filtresi
      const cat = getCategoryForIssue(it);
      if (fCategory === "__none__" && cat) return false;
      if (fCategory && fCategory !== "__none__" && (!cat || cat.id !== fCategory)) return false;

      // Tarih aralığı (çözüm tarihi)
      const ra = it.resolvedAt?.seconds ? new Date(it.resolvedAt.seconds * 1000) : null;
      if (fFrom && ra && ra < new Date(fFrom + "T00:00:00")) return false;
      if (fTo && ra && ra > new Date(fTo + "T23:59:59")) return false;

      // Süresinde / Geç filtresi
      if (onTimeState !== "all") {
        const dur = dayDiff(it.date, it.resolvedAt);
        const terminDays = typeof cat?.days === "number" ? cat.days : null;
        if (terminDays == null) return false; // termin yoksa bu filtreye dahil etme
        const onTime = dur != null && dur <= terminDays;
        if (onTimeState === "ontime" && !onTime) return false;
        if (onTimeState === "late" && onTime) return false;
      }

      return true;
    });
  }, [issues, fTesis, fPlate, fCreator, fResolver, fText, fFrom, fTo, onTimeState, fCategory, terminMap, titleMap]);

  // Gruplama: Tesis > Plaka
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((it) => {
      const tesisKey = it.tesis || "Belirtilmemiş";
      if (!g[tesisKey]) g[tesisKey] = {};
      if (!g[tesisKey][it.plate]) g[tesisKey][it.plate] = [];
      g[tesisKey][it.plate].push(it);
    });
    return g;
  }, [filtered]);

  // Özet
  const summary = useMemo(() => {
    const count = filtered.length;
    const durations = filtered.map(x => dayDiff(x.date, x.resolvedAt)).filter(d => typeof d === "number" && !Number.isNaN(d));
    const avg = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
    const byTesis = filtered.reduce((acc, it) => {
      const k = it.tesis || "Belirtilmemiş";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return { count, avg, byTesis };
  }, [filtered]);

  // Excel
  const exportToExcel = () => {
    const rows = filtered.map((x) => {
      const dur = dayDiff(x.date, x.resolvedAt);
      const cat = getCategoryForIssue(x);
      const terminDays = typeof cat?.days === "number" ? cat.days : null;
      const onTime = terminDays != null && dur != null ? (dur <= terminDays) : null;

      return {
        Tesis: x.tesis || "-",
        Plaka: x.plate,
        Eksiklik: x.issue,
        "Araç Cinsi": x.aracCinsi || "-",
        Model: x.model || "-",
        Kategori: cat ? cat.name : "-",
        "Termin (gün)": terminDays ?? "-",
        "Oluşturma Tarihi": fmtDate(x.date),
        Ekleyen: x.createdBy || "-",
        "Çözüm Tarihi": fmtDate(x.resolvedAt),
        Çözen: x.resolvedBy || "-",
        "Süre (gün)": dur ?? "-",
        "Süre/Termin": terminDays != null && dur != null ? `${dur} gün / ${terminDays} gün` : "-",
        "Süresinde Çözüldü": onTime == null ? "-" : onTime ? "Evet" : "Hayır",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cozulenler");

    const sumRows = [
      { Metrik: "Filtrelenen", Deger: summary.count },
      { Metrik: "Ortalama Gün", Deger: summary.avg ?? "-" },
      {},
      { Metrik: "Tesis", Deger: "Adet" },
      ...Object.entries(summary.byTesis).map(([k, v]) => ({ Metrik: k, Deger: v })),
    ];
    const ws2 = XLSX.utils.json_to_sheet(sumRows);
    XLSX.utils.book_append_sheet(wb, ws2, "Ozet");

    XLSX.writeFile(wb, "cozulen-uygunsuzluklar.xlsx");
  };

  // Kategori seçenekleri
  const categoryOptions = useMemo(() => {
    const arr = Object.values(terminMap).sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
    return arr;
  }, [terminMap]);

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ textAlign: "center", marginBottom: 12 }}>Çözülen Uygunsuzluklar</h2>

      {/* Filtreler */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 12,
          alignItems: "center",
        }}
      >
        <select value={fTesis} onChange={(e) => setFTesis(e.target.value)} style={fieldStyle}>
          <option value="">Tüm Tesisler</option>
          {tesisler.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <Autocomplete
          value={fPlate}
          onCommit={setFPlate}
          options={plates}
          placeholder="Tüm plakalar"
        />

        <input
          type="text"
          placeholder="Metin içinde ara"
          value={fText}
          onChange={(e) => setFText(e.target.value)}
          style={fieldStyle}
        />
        <input
          type="text"
          placeholder="Ekleyen (mail)"
          value={fCreator}
          onChange={(e) => setFCreator(e.target.value)}
          style={fieldStyle}
        />
        <input
          type="text"
          placeholder="Çözen (mail)"
          value={fResolver}
          onChange={(e) => setFResolver(e.target.value)}
          style={fieldStyle}
        />

        <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={fieldStyle} />
        <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={fieldStyle} />

        <select value={onTimeState} onChange={(e) => setOnTimeState(e.target.value)} style={fieldStyle}>
          <option value="all">Tümü</option>
          <option value="ontime">Süresinde Çözülenler</option>
          <option value="late">Süresinde Çözülemeyenler</option>
        </select>

        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} style={fieldStyle}>
          <option value="">Kategori: Tümü</option>
          <option value="__none__">Kategori: Kategorisiz</option>
          {categoryOptions.map(c => (
            <option key={c.id} value={c.id}>{c.name} {typeof c.days === "number" ? `(${c.days}g)` : ""}</option>
          ))}
        </select>

        <button
          onClick={exportToExcel}
          style={{ padding: "8px 12px", border: "none", borderRadius: 6, cursor: "pointer", background: "#3b82f6", color: "#fff" }}
        >
          Excel'e Aktar
        </button>
      </div>

      {/* Özet */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <span style={chip}>Filtrelenen: <b>{summary.count}</b></span>
        <span style={chip}>Ortalama Gün: <b>{summary.avg ?? "-"}</b></span>
        {Object.entries(summary.byTesis).map(([k, v]) => (
          <span key={k} style={chip}>{k}: <b>{v}</b></span>
        ))}
      </div>

      {/* Tablo */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 1050, fontFamily: "Arial" }}>
          <thead>
            <tr>
              <th style={th}>Tesis</th>
              <th style={th}>Plaka</th>
              <th style={th}>Eksiklik</th>
              <th style={th}>Kategori</th>
              <th style={th}>Ekleyen</th>
              <th style={th}>Oluşturma</th>
              <th style={th}>Çözen</th>
              <th style={th}>Çözüm Tarihi</th>
              <th style={th}>Süre / Termin</th>
              <th style={th}>Süresinde Çözüldü</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} style={{ padding: 12, textAlign: "center" }}>Yükleniyor...</td></tr>
            )}
            {!loading && Object.keys(grouped).length === 0 && (
              <tr><td colSpan={10} style={{ padding: 12, textAlign: "center" }}>Kayıt bulunamadı</td></tr>
            )}
            {!loading && Object.entries(grouped).map(([tesis, plateGroup]) => {
              const tesisSpan = Object.values(plateGroup).reduce((a, arr) => a + arr.length, 0);
              let tesisPrinted = false;

              return Object.entries(plateGroup).flatMap(([plate, arr]) =>
                arr.map((it, idx) => {
                  const dur = dayDiff(it.date, it.resolvedAt);
                  const cat = getCategoryForIssue(it);
                  const terminDays = typeof cat?.days === "number" ? cat.days : null;
                  const onTime = terminDays != null && dur != null ? (dur <= terminDays) : null;

                  const row = [];

                  if (!tesisPrinted) {
                    row.push(
                      <td key={`tesis-${tesis}`} rowSpan={tesisSpan} style={{ ...td, fontWeight: 700, background: "#f1f5f9", verticalAlign: "top" }}>
                        {tesis}
                      </td>
                    );
                    tesisPrinted = true;
                  }

                  if (idx === 0) {
                    row.push(
                      <td key={`plate-${plate}`} rowSpan={arr.length} style={{ ...td, fontWeight: 700, background: "#e2e8f0", verticalAlign: "top" }}>
                        {plate}
                      </td>
                    );
                  }

                  row.push(
                    <td key={`issue-${it.id}`} style={{ ...td, verticalAlign: "top" }}>
                      <div style={{ fontWeight: 600 }}>{it.issue}</div>
                      <div style={{ fontSize: 12, color: "#475569" }}>
                        Araç Cinsi: {it.aracCinsi || "-"} | Model: {it.model || "-"}
                      </div>
                    </td>
                  );

                  row.push(
                    <td key={`cat-${it.id}`} style={{ ...td, verticalAlign: "top" }}>
                      {cat ? `${cat.name}${typeof cat.days === "number" ? ` (${cat.days}g)` : ""}` : "-"}
                    </td>
                  );

                  row.push(<td key={`creator-${it.id}`} style={td}>{it.createdBy || "-"}</td>);
                  row.push(<td key={`createdAt-${it.id}`} style={td}>{fmtDate(it.date)}</td>);
                  row.push(<td key={`resolver-${it.id}`} style={td}>{it.resolvedBy || "-"}</td>);
                  row.push(<td key={`resolvedAt-${it.id}`} style={td}>{fmtDate(it.resolvedAt)}</td>);

                  row.push(
                    <td key={`durterm-${it.id}`} style={{ ...td, verticalAlign: "top" }}>
                      {terminDays != null && dur != null ? `${dur} gün / ${terminDays} gün` : "-"}
                    </td>
                  );

                  row.push(
                    <td key={`ontime-${it.id}`} style={{ ...td, textAlign: "center" }}>
                      {onTime == null ? "" : onTime ? "✅" : "❌"}
                    </td>
                  );

                  return <tr key={it.id}>{row}</tr>;
                })
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResolvedIssues;
